import { scaleLinear, scaleTime } from "@visx/scale";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { CHART_CONFIG } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/constants";
import {
  ChartScales,
  DateScale,
  InfraEventPassengerCount,
  ValueScale,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/types";
import {
  computeNiceStep,
  findClosestDataPoint,
  getDate,
  getMousePosition,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/utils";
import { useMemo, useState } from "react";

// Hook for chart scales
export const useChartScales = (
  data: InfraEventPassengerCount[],
  xMax: number,
  yMax: number,
  maxYValue: number,
): ChartScales => {
  const dateDomain = useMemo<[Date, Date]>(() => {
    if (!data.length) {
      const now = new Date();
      return [now, now];
    }

    const { min, max } = data.reduce(
      (acc, d) => {
        const t = getDate(d).getTime();
        return {
          min: Math.min(acc.min, t),
          max: Math.max(acc.max, t),
        };
      },
      { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY },
    );

    return [new Date(min), new Date(max)];
  }, [data]);

  const dateScale = useMemo(
    () =>
      scaleTime({
        range: [0, xMax],
        domain: dateDomain,
      }),
    [dateDomain, xMax],
  );

  // Use configured desired tick count
  const { niceMax: configuredNiceMax } = computeNiceStep(
    maxYValue,
    CHART_CONFIG.desiredTickCount,
  );

  const checkedNiceMax = configuredNiceMax > 9 ? configuredNiceMax : 10; // Ensure at least 10 for better scaling when maxYValue is low

  const valueScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: [0, checkedNiceMax],
      }),
    [yMax, checkedNiceMax],
  );

  return { dateScale, valueScale };
};

// Hook for tooltip functionality
export const useChartTooltip = (
  data: InfraEventPassengerCount[],
  dateScale: DateScale,
  valueScale: ValueScale,
) => {
  const tooltip = useTooltip<InfraEventPassengerCount>();
  const tooltipPortal = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const [hoverX, setHoverX] = useState<number | null>(null);

  const handleTooltip = (event: React.MouseEvent<SVGElement>) => {
    if (data.length === 0) {
      return;
    }

    const { x } = getMousePosition(event);
    const xInsideGroup = x - CHART_CONFIG.margin.left;
    setHoverX(xInsideGroup);
    const x0 = dateScale.invert(xInsideGroup);
    const closestPoint = findClosestDataPoint(data, x0);

    tooltip.showTooltip({
      tooltipData: closestPoint,
      tooltipLeft: dateScale(getDate(closestPoint)) + CHART_CONFIG.margin.left,
      tooltipTop: valueScale(closestPoint.passengers) + CHART_CONFIG.margin.top,
    });
  };

  return {
    ...tooltip,
    ...tooltipPortal,
    handleTooltip,
    hoverX,
  };
};
